import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building, 
  FileCheck, 
  Globe, 
  DollarSign, 
  Upload, 
  CheckCircle,
  FileText,
  Users,
  Award,
  MapPin,
  Clock,
  Shield
} from 'lucide-react';

const VerifierRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    companyType: '',
    registrationNumber: '',
    taxId: '',
    foundedYear: '',
    employeeCount: '',
    website: '',
    
    // Contact Information
    primaryContact: {
      name: '',
      title: '',
      email: '',
      phone: ''
    },
    headquarters: {
      country: '',
      city: '',
      address: '',
      zipCode: ''
    },
    
    // Credentials & Certifications
    accreditations: [],
    certifications: [],
    memberships: [],
    insuranceCoverage: '',
    insuranceAmount: '',
    
    // Verification Capabilities
    supportedStandards: [],
    verificationTypes: [],
    geographicCoverage: [],
    languages: [],
    methodologies: [],
    
    // Infrastructure & Team
    teamSize: '',
    seniorExperts: '',
    technicalCapabilities: [],
    equipmentResources: '',
    qualityAssurance: '',
    
    // Experience & Track Record
    yearsOfExperience: '',
    totalProjectsVerified: '',
    industryExperience: [],
    significantProjects: '',
    clientReferences: [],
    
    // Pricing & Commercial
    pricingModel: '',
    standardRates: {
      forestry: '',
      renewable: '',
      efficiency: '',
      other: ''
    },
    paymentTerms: '',
    currencyPreferences: [],
    
    // Documents & Compliance
    companyRegistration: null,
    accreditationCertificates: [],
    insuranceDocuments: null,
    sampleReports: [],
    teamCVs: [],
    
    // Platform Integration
    integrationType: '',
    apiRequirements: '',
    reportingFrequency: '',
    communicationPreferences: [],
    
    // Legal & Compliance
    dataProtectionCompliance: false,
    conflictOfInterestPolicy: null,
    ethicsPolicy: null,
    termsAccepted: false,
    privacyAccepted: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verificationStandards = [
    'Gold Standard', 'VCS (Verified Carbon Standard)', 'CDM (Clean Development Mechanism)',
    'American Carbon Registry (ACR)', 'Climate Action Reserve (CAR)', 'Plan Vivo',
    'Social Carbon', 'CCBS (Climate, Community & Biodiversity Standards)', 'Other'
  ];

  const verificationTypes = [
    'Forest Conservation', 'Reforestation', 'Renewable Energy', 'Energy Efficiency',
    'Methane Capture', 'Blue Carbon', 'Soil Carbon', 'Direct Air Capture',
    'Waste Management', 'Transportation', 'Industrial Processes'
  ];

  const technicalCapabilities = [
    'Remote Sensing & GIS', 'Field Surveys', 'Laboratory Analysis', 'Drone Technology',
    'Satellite Monitoring', 'IoT Sensors', 'Blockchain Verification', 'AI/ML Analytics',
    'Environmental Modeling', 'Social Impact Assessment'
  ];

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Brazil',
    'India', 'China', 'Australia', 'Kenya', 'South Africa', 'Mexico', 'Other'
  ];

  const steps = [
    { number: 1, title: 'Company Info', icon: Building },
    { number: 2, title: 'Credentials', icon: FileCheck },
    { number: 3, title: 'Capabilities', icon: Globe },
    { number: 4, title: 'Experience', icon: Award },
    { number: 5, title: 'Commercial', icon: DollarSign },
    { number: 6, title: 'Documents', icon: FileText },
    { number: 7, title: 'Review', icon: CheckCircle }
  ];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleArrayChange = (field, value, isChecked) => {
    setFormData(prev => ({
      ...prev,
      [field]: isChecked 
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const handleFileUpload = (field, files) => {
    setFormData(prev => ({
      ...prev,
      [field]: files
    }));
  };

  const validateStep = (step) => {
    const stepErrors = {};

    switch (step) {
      case 1:
        if (!formData.companyName) stepErrors.companyName = 'Company name is required';
        if (!formData.companyType) stepErrors.companyType = 'Company type is required';
        if (!formData.primaryContact.name) stepErrors['primaryContact.name'] = 'Primary contact name is required';
        if (!formData.primaryContact.email) stepErrors['primaryContact.email'] = 'Primary contact email is required';
        if (!formData.headquarters.country) stepErrors['headquarters.country'] = 'Country is required';
        break;
      case 2:
        if (formData.accreditations.length === 0) stepErrors.accreditations = 'At least one accreditation is required';
        if (!formData.insuranceCoverage) stepErrors.insuranceCoverage = 'Insurance coverage is required';
        break;
      case 3:
        if (formData.supportedStandards.length === 0) stepErrors.supportedStandards = 'At least one verification standard is required';
        if (formData.verificationTypes.length === 0) stepErrors.verificationTypes = 'At least one verification type is required';
        if (formData.geographicCoverage.length === 0) stepErrors.geographicCoverage = 'Geographic coverage is required';
        break;
      case 4:
        if (!formData.yearsOfExperience) stepErrors.yearsOfExperience = 'Years of experience is required';
        if (!formData.totalProjectsVerified) stepErrors.totalProjectsVerified = 'Total projects verified is required';
        break;
      case 5:
        if (!formData.pricingModel) stepErrors.pricingModel = 'Pricing model is required';
        break;
      case 6:
        if (!formData.companyRegistration) stepErrors.companyRegistration = 'Company registration document is required';
        if (!formData.termsAccepted) stepErrors.termsAccepted = 'Terms must be accepted';
        if (!formData.privacyAccepted) stepErrors.privacyAccepted = 'Privacy policy must be accepted';
        break;
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 7));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(6)) return;

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      console.log('Submitting verifier application:', formData);
      
      setTimeout(() => {
        setCurrentStep(7);
        alert('Application submitted successfully! We will review your application and contact you within 5-7 business days.');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Company Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter company name"
                />
                {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Type *
                </label>
                <select
                  value={formData.companyType}
                  onChange={(e) => handleInputChange('companyType', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.companyType ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select company type</option>
                  <option value="private-corporation">Private Corporation</option>
                  <option value="public-company">Public Company</option>
                  <option value="non-profit">Non-Profit Organization</option>
                  <option value="government-agency">Government Agency</option>
                  <option value="consulting-firm">Consulting Firm</option>
                  <option value="research-institution">Research Institution</option>
                  <option value="other">Other</option>
                </select>
                {errors.companyType && <p className="text-red-500 text-xs mt-1">{errors.companyType}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Number
                </label>
                <input
                  type="text"
                  value={formData.registrationNumber}
                  onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Company registration number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Founded Year
                </label>
                <input
                  type="number"
                  min="1900"
                  max="2024"
                  value={formData.foundedYear}
                  onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 2010"
                />
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Primary Contact</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.primaryContact.name}
                    onChange={(e) => handleInputChange('primaryContact.name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors['primaryContact.name'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Contact person name"
                  />
                  {errors['primaryContact.name'] && <p className="text-red-500 text-xs mt-1">{errors['primaryContact.name']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title/Position
                  </label>
                  <input
                    type="text"
                    value={formData.primaryContact.title}
                    onChange={(e) => handleInputChange('primaryContact.title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., CEO, Director"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.primaryContact.email}
                    onChange={(e) => handleInputChange('primaryContact.email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors['primaryContact.email'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="contact@company.com"
                  />
                  {errors['primaryContact.email'] && <p className="text-red-500 text-xs mt-1">{errors['primaryContact.email']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.primaryContact.phone}
                    onChange={(e) => handleInputChange('primaryContact.phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Headquarters Location</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <select
                    value={formData.headquarters.country}
                    onChange={(e) => handleInputChange('headquarters.country', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors['headquarters.country'] ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  {errors['headquarters.country'] && <p className="text-red-500 text-xs mt-1">{errors['headquarters.country']}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.headquarters.city}
                    onChange={(e) => handleInputChange('headquarters.city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter city"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Credentials & Certifications</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accreditations *
              </label>
              <div className="space-y-2">
                {['ISO 14064', 'ISO 14065', 'ANSI National Accreditation Board', 'UNFCCC DOE', 'Other'].map(accreditation => (
                  <label key={accreditation} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.accreditations.includes(accreditation)}
                      onChange={(e) => handleArrayChange('accreditations', accreditation, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{accreditation}</span>
                  </label>
                ))}
              </div>
              {errors.accreditations && <p className="text-red-500 text-xs mt-1">{errors.accreditations}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Coverage Type *
                </label>
                <select
                  value={formData.insuranceCoverage}
                  onChange={(e) => handleInputChange('insuranceCoverage', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.insuranceCoverage ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select insurance type</option>
                  <option value="professional-liability">Professional Liability</option>
                  <option value="errors-omissions">Errors & Omissions</option>
                  <option value="general-liability">General Liability</option>
                  <option value="comprehensive">Comprehensive Coverage</option>
                </select>
                {errors.insuranceCoverage && <p className="text-red-500 text-xs mt-1">{errors.insuranceCoverage}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Insurance Amount (USD)
                </label>
                <input
                  type="number"
                  value={formData.insuranceAmount}
                  onChange={(e) => handleInputChange('insuranceAmount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 1000000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Professional Memberships
              </label>
              <div className="space-y-2">
                {['International Association for Impact Assessment', 'Society of Environmental Toxicology and Chemistry', 'Association of Climate Change Officers', 'Other'].map(membership => (
                  <label key={membership} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.memberships.includes(membership)}
                      onChange={(e) => handleArrayChange('memberships', membership, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{membership}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Verification Capabilities</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supported Verification Standards *
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {verificationStandards.map(standard => (
                  <label key={standard} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={formData.supportedStandards.includes(standard)}
                      onChange={(e) => handleArrayChange('supportedStandards', standard, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">{standard}</span>
                  </label>
                ))}
              </div>
              {errors.supportedStandards && <p className="text-red-500 text-xs mt-1">{errors.supportedStandards}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Types You Can Verify *
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {verificationTypes.map(type => (
                  <label key={type} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={formData.verificationTypes.includes(type)}
                      onChange={(e) => handleArrayChange('verificationTypes', type, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">{type}</span>
                  </label>
                ))}
              </div>
              {errors.verificationTypes && <p className="text-red-500 text-xs mt-1">{errors.verificationTypes}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geographic Coverage *
              </label>
              <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {countries.map(country => (
                  <label key={country} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={formData.geographicCoverage.includes(country)}
                      onChange={(e) => handleArrayChange('geographicCoverage', country, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">{country}</span>
                  </label>
                ))}
              </div>
              {errors.geographicCoverage && <p className="text-red-500 text-xs mt-1">{errors.geographicCoverage}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Technical Capabilities
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {technicalCapabilities.map(capability => (
                  <label key={capability} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={formData.technicalCapabilities.includes(capability)}
                      onChange={(e) => handleArrayChange('technicalCapabilities', capability, e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <span className="ml-2">{capability}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Experience & Track Record</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Years of Experience *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.yearsOfExperience}
                  onChange={(e) => handleInputChange('yearsOfExperience', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.yearsOfExperience ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 15"
                />
                {errors.yearsOfExperience && <p className="text-red-500 text-xs mt-1">{errors.yearsOfExperience}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Projects Verified *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.totalProjectsVerified}
                  onChange={(e) => handleInputChange('totalProjectsVerified', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.totalProjectsVerified ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., 250"
                />
                {errors.totalProjectsVerified && <p className="text-red-500 text-xs mt-1">{errors.totalProjectsVerified}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Size
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.teamSize}
                  onChange={(e) => handleInputChange('teamSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 25"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senior Experts
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.seniorExperts}
                  onChange={(e) => handleInputChange('seniorExperts', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 8"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Significant Projects Description
              </label>
              <textarea
                value={formData.significantProjects}
                onChange={(e) => handleInputChange('significantProjects', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Describe 2-3 of your most significant verification projects, including project scale, complexity, and outcomes..."
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Commercial Terms</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pricing Model *
              </label>
              <select
                value={formData.pricingModel}
                onChange={(e) => handleInputChange('pricingModel', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.pricingModel ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select pricing model</option>
                <option value="fixed-project">Fixed Price per Project</option>
                <option value="per-credit">Price per Credit Verified</option>
                <option value="hourly-rate">Hourly Rate</option>
                <option value="hybrid">Hybrid Model</option>
              </select>
              {errors.pricingModel && <p className="text-red-500 text-xs mt-1">{errors.pricingModel}</p>}
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Standard Rates (USD)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Forestry Projects
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.standardRates.forestry}
                    onChange={(e) => handleInputChange('standardRates.forestry', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 0.50 per credit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renewable Energy
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.standardRates.renewable}
                    onChange={(e) => handleInputChange('standardRates.renewable', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 0.30 per credit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Energy Efficiency
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.standardRates.efficiency}
                    onChange={(e) => handleInputChange('standardRates.efficiency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 0.40 per credit"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Other Projects
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.standardRates.other}
                    onChange={(e) => handleInputChange('standardRates.other', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="e.g., 0.45 per credit"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Terms
              </label>
              <select
                value={formData.paymentTerms}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Select payment terms</option>
                <option value="net-30">Net 30 days</option>
                <option value="net-15">Net 15 days</option>
                <option value="50-50">50% upfront, 50% on completion</option>
                <option value="milestone">Milestone-based payments</option>
              </select>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Documents & Legal</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Registration Document *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500">PDF, JPG, PNG up to 10MB</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload('companyRegistration', e.target.files[0])}
                  className="hidden"
                />
              </div>
              {errors.companyRegistration && <p className="text-red-500 text-xs mt-1">{errors.companyRegistration}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accreditation Certificates
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Upload multiple certificates</p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload('accreditationCertificates', Array.from(e.target.files))}
                  className="hidden"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="dataProtection"
                  checked={formData.dataProtectionCompliance}
                  onChange={(e) => handleInputChange('dataProtectionCompliance', e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="dataProtection" className="ml-2 text-sm text-gray-700">
                  We comply with GDPR and other applicable data protection regulations
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                  I accept the <Link to="/terms" className="text-green-600 hover:text-green-700">Terms of Service</Link> *
                </label>
              </div>
              {errors.termsAccepted && <p className="text-red-500 text-xs mt-1">{errors.termsAccepted}</p>}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={formData.privacyAccepted}
                  onChange={(e) => handleInputChange('privacyAccepted', e.target.checked)}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="privacy" className="ml-2 text-sm text-gray-700">
                  I accept the <Link to="/privacy" className="text-green-600 hover:text-green-700">Privacy Policy</Link> *
                </label>
              </div>
              {errors.privacyAccepted && <p className="text-red-500 text-xs mt-1">{errors.privacyAccepted}</p>}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
              <p className="text-gray-600">
                Thank you for applying to become a verified platform verifier. We will review your application and contact you within 5-7 business days.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg text-left">
              <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Initial document review (2-3 business days)</li>
                <li>2. Reference and credential verification (3-5 business days)</li>
                <li>3. Technical capability assessment (1-2 days)</li>
                <li>4. Final approval and platform onboarding (1-2 days)</li>
              </ol>
            </div>

            <div className="space-y-3">
              <Link
                to="/marketplace"
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                Browse Marketplace
              </Link>
              <Link
                to="/"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
              >
                Return Home
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link to="/marketplace" className="mr-4">
            <ArrowLeft className="h-6 w-6 text-gray-600 hover:text-gray-800" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Become a Platform Verifier</h1>
            <p className="text-gray-600">Join our network of trusted carbon credit verification experts</p>
          </div>
        </div>

        {/* Progress Steps */}
        {currentStep < 7 && (
          <div className="flex items-center justify-between mb-8 overflow-x-auto">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center min-w-0">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.number 
                    ? 'bg-green-600 border-green-600 text-white' 
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.number ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.number ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    Step {step.number}
                  </p>
                  <p className={`text-xs ${
                    currentStep >= step.number ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`hidden sm:block w-8 h-0.5 ml-4 ${
                    currentStep > step.number ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <form onSubmit={handleSubmit}>
          {renderStepContent()}

          {/* Navigation Buttons */}
          {currentStep < 7 && (
            <div className="flex justify-between mt-8 pt-6 border-t">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`px-6 py-2 border border-gray-300 rounded-lg font-medium ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>

              {currentStep < 6 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition duration-200"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2 font-medium rounded-lg transition duration-200 ${
                    isSubmitting
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Info Panel */}
      {currentStep < 7 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-start">
            <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Platform Benefits</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Access to a global marketplace of carbon credit projects</li>
                <li>• Integrated verification workflow and management tools</li>
                <li>• Automated payment processing and escrow services</li>
                <li>• Marketing and visibility to project developers worldwide</li>
                <li>• Technical support and platform training</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifierRegistration; 